'use client';

import React from 'react';
import {
  Stack,
  Table as MuiTable,
  TableContainer,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableHead as MuiTableHeader,
  TableRow as MuiTableRow,
  Typography,
} from '@unity/cloud-ui';

const getAreAllChildrenInlineElements = (children?: React.ReactElement): boolean => {
  if (!children) {
    return false;
  }

  const inlineElements = ['strong', 'em', 'a', 'span', 'mark', 'del', 'ins', 'sub', 'sup'];

  if (typeof children === 'string') {
    return true;
  }
  if (inlineElements.includes(children.type as string)) {
    return true;
  }
  if (Array.isArray(children)) {
    return children.every((child) => getAreAllChildrenInlineElements(child));
  }
  return false;
};

export const TableCell = (props: React.PropsWithChildren) => {
  const { children, ...rest } = props;

  const areAllChildrenInlineElements = getAreAllChildrenInlineElements(children as React.ReactElement);

  return (
    <MuiTableCell {...rest}>{areAllChildrenInlineElements ? children : <Stack gap={1}>{children}</Stack>}</MuiTableCell>
  );
};

export const TableRow = MuiTableRow;

export const TableBody = (props) => {
  return (
    <MuiTableBody
      sx={({ vars }) => ({
        '& tr': {
          // make each other row a different color
          '&:nth-of-type(odd)': {
            backgroundColor: vars.palette.background.default,
          },
          '&:nth-of-type(even)': {
            backgroundColor: vars.palette.background.paperElevation0,
          },
        },
      })}
      {...props}
    />
  );
};

export const Table = (props) => {
  const { caption, children } = props;

  return (
    <TableContainer>
      <MuiTable
        sx={{
          minWidth: '100%',
          border: (theme) => `1px solid ${theme.vars.palette.divider}`,
          '& th p, & td p': {
            fontFamily: 'inherit',
            fontWeight: 'inherit',
            fontSize: '.875rem',
            lineHeight: 'inherit',
            letterSpacing: 'inherit',
          },
        }}
      >
        {caption ? <caption>{caption}</caption> : null}
        {children}
      </MuiTable>
    </TableContainer>
  );
};

export const TableHeader = (props) => {
  return (
    <MuiTableHeader
      sx={({ vars }) => ({
        '& .MuiTableCell-head': { backgroundColor: vars.palette.primary.lighter },
      })}
      {...props}
    />
  );
};

export const TableHeaderCell = (props) => {
  const { children } = props;

  return (
    <TableCell>
      <Typography variant="h4">{children}</Typography>
    </TableCell>
  );
};
